import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useDebouncedCallback } from "use-debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Topbar,
  Button,
  SearchField,
  CategoriesDropdown,
  DashboardDatePicker,
  ServerPaginationTable,
  CustomCellRendererWithSkeleton,
  TooltipWrapper,
  TableMultiValueField,
} from "../../../shared/components";
import {
  EMPTY_STRING,
  skeletonArray,
  globalConstants,
  shopifyConstants,
  defaultGridState,
  dashboardConstants,
  categoryDropdownConstants,
  TABLE_IDS,
  getShopifyOrderPaymentStatusOptions,
  getShopifyOrderFulfillmentStatusOptions,
} from "../../../constants";
import { PRODUCT_TYPES_ENUMS } from "../../../system/globalEnums";
import {
  ShowIcon,
  CrossIcon,
  PrintIcon,
  SearchIcon,
  BlueBoxIcon,
  ActionDotsIcons,
} from "../../../assets/icons/iconsProvider";
import {
  getRowId,
  getRowStyle,
  getTimeFrame,
  isButtonDisable,
  isSpinnerEnabled,
  LoadingIndicator,
  getReceiptPrintDate,
  getShopifyOrderActions,
  getTotalLineProductsAndQuantity,
  prepareFilterForShopifyOrdersCall,
  getShopifyOrderFulfillmentStatus,
  handlePrintShopifyPackingSlips,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleIsShopifyOrderCancelled,
  handleTransferToLocation,
  getCustomerFullName,
  getActionColumnOfAgGridReact,
  hasItemsFromOtherLocations,
  getShopifyOrderLocationNames,
} from "../../../shared/utility";
import { shopifyActions } from "../../../redux/actions";
import OrderDetailsModal from "./OrderDetailsModal/OrderDetailsModal";
import DropdownSkeleton from "../../../shared/Skeletons/DropdownSkeleton/DropdownSkeleton";
import ClickableTooltip from "../../../shared/components/toolTipComponent/ClickableTooltip";
import ConfirmationModal from "../../Inventory/Items/ConfirmationModal/ConfirmationModal";

const ALL_OPTION = {
  label: globalConstants.ALL,
  value: globalConstants.ALL,
};

const Orders = ({
  currentStore,
  spinnerArray,
  shopifyOrders,
  getShopifyOrders,
  updateShopifyOrderStatus,
  currentStoreCompany,
}) => {
  //-------- Grid ref
  const gridRef = useRef();
  const tooltipRef = useRef();
  const onConfirmActionRef = useRef(null);

  //------- usestates
  const [orderDetail, setOrderDetail] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridState, setGridState] = useState({
    ...defaultGridState,
    locationFilter: shopifyConstants.LOCATION_FILTER.CURRENT_LOCATION,
  });
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isShowOrderDetail, setIsShowOrderDetail] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(EMPTY_STRING);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [confirmationModalMessage, setConfirmationModalMessage] =
    useState(EMPTY_STRING);
  const [orderType, setOrderType] = useState(ALL_OPTION);
  const [orderPaymentStatus, setOrderPaymentStatus] = useState(ALL_OPTION);
  const [orderFulfillmentStatus, setOrderFulfillmentStatus] =
    useState(ALL_OPTION);
  const [timeFrame, setTimeFrame] = useState({
    start: "",
    end: "",
    duration: dashboardConstants.DAILY,
  });

  const toggleConfirmationModal = () => {
    setIsConfirmationModal((prev) => !prev);
  };

  const handleStatusChangeWithConfirmation = (actionFn, orderOrOrders) => {
    const currentStoreLocationId =
      currentStore?.shopify?.location?.shopifyLocationId;
    const isBulk = Array.isArray(orderOrOrders);
    const ordersToCheck = isBulk ? orderOrOrders : [orderOrOrders];

    const hasOtherLoc = ordersToCheck.some((order) =>
      hasItemsFromOtherLocations(order, currentStoreLocationId)
    );

    if (!hasOtherLoc) {
      actionFn();
      return;
    }

    setConfirmationModalMessage(
      isBulk
        ? shopifyConstants.LOCATION_CONFIRMATION.BULK_ORDER_MESSAGE
        : shopifyConstants.LOCATION_CONFIRMATION.SINGLE_ORDER_MESSAGE
    );
    onConfirmActionRef.current = actionFn;
    toggleConfirmationModal();
  };

  function actionCellRenderer(params) {
    if (params.data?.id) {
      const actionButtons = getShopifyOrderActions(
        params?.data,
        currentStore,
        gridState,
        updateShopifyOrderStatus,
        handleMarkOrderAsOpen,
        handleMarkOrderAsReady,
        handleMarkOrderAsFulfilled,
        handleTransferToLocation,
        handleStatusChangeWithConfirmation
      );
      return (
        <div
          onMouseLeave={() => {
            tooltipRef.current?.close();
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOrderDetail(params?.data);
              toggleOrderDetailModal();
            }}
            className={`action-Button-Wrapper view-order-icon`}
            disabled={false}
          >
            <ShowIcon className="cursor-pointer" />
          </button>

          <>
            {actionButtons.props?.children?.length > 0 && (
              <TooltipWrapper>
                <ClickableTooltip
                  anchorSelect={`#tool${params.data.id}`}
                  openOnClick={true}
                  tooltipRef={tooltipRef}
                >
                  {actionButtons}
                </ClickableTooltip>
              </TooltipWrapper>
            )}
            <button
              id={`tool${params.data.id}`}
              className={`action-Button-Wrapper ${
                actionButtons.props?.children?.length > 0
                  ? "cursor-pointer"
                  : "disabled-icon"
              }`}
              disabled={actionButtons.props?.children?.length <= 0}
              onClick={() => {
                if (tooltipRef.current?.isOpen) {
                  tooltipRef.current.close();
                } else {
                  tooltipRef.current?.open({
                    anchorSelect: `tool${params.data.id}`,
                  });
                }
              }}
            >
              <ActionDotsIcons
                className={
                  actionButtons.props?.children?.length > 0
                    ? "cursor-pointer"
                    : "disabled-icon"
                }
              />
            </button>
          </>
        </div>
      );
    } else {
      return <DropdownSkeleton width="5em" height="1.5em" />;
    }
  }

  //------- Columns for Orders Grid
  const OrdersColumns = useMemo(
    () => {
      const commonClassesForRowCell = (params) => `inventory-cell-max-size 
           ${handleIsShopifyOrderCancelled(params?.data, true)}
          `;
      const isAllLocations =
        gridState?.locationFilter ===
        shopifyConstants.LOCATION_FILTER.ALL_LOCATIONS;

      return [
        {
          headerName: "Date",
          headerTooltip: "Date",
          field: "createdOn",
          tooltipValueGetter: (params) =>
            getReceiptPrintDate(params?.data?.createdOn || EMPTY_STRING),
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.createdOn}
              cellComponent={getReceiptPrintDate(params?.data?.createdOn)}
            />
          ),
          checkboxSelection: true,
          headerCheckboxSelection: true,
        },
        {
          headerName: "Customer",
          headerTooltip: "Customer",
          field: "customer",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.id}
              cellComponent={
                <span className={commonClassesForRowCell(params)}>
                  {getCustomerFullName(params?.data?.customer, true)}
                </span>
              }
            />
          ),
        },
        ...(isAllLocations
          ? [
              {
                headerName: "Location(s)",
                headerTooltip: "Location(s)",
                field: "locationNames",
                tooltipValueGetter: (params) => {
                  const locationNames = getShopifyOrderLocationNames(
                    params?.data
                  );
                  return locationNames.length > 1
                    ? EMPTY_STRING
                    : locationNames[0] || EMPTY_STRING;
                },
                cellRenderer: (params) => (
                  <CustomCellRendererWithSkeleton
                    params={params}
                    componentRenderCondition={params?.data?.id}
                    cellComponent={
                      <TableMultiValueField
                        label="Locations"
                        anchorId={`location-tool-${params?.data?.id}`}
                        values={getShopifyOrderLocationNames(params?.data)}
                        className={commonClassesForRowCell(params)}
                        valueClassName={commonClassesForRowCell(params)}
                      />
                    }
                  />
                ),
              },
            ]
          : []),
        {
          headerTooltip: "Order Type",
          field: "orderType",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.id}
              cellComponent={
                <span className={commonClassesForRowCell(params)}>
                  {params?.data?.orderType}
                </span>
              }
            />
          ),
        },
        {
          headerTooltip: "# of Items",
          field: "noOfItem",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.line_items?.length}
              cellComponent={
                <span className={commonClassesForRowCell(params)}>
                  {getTotalLineProductsAndQuantity(params?.data)?.totalQuantity}
                </span>
              }
            />
          ),
        },
        {
          headerTooltip: "Order Total",
          field: "orderTotal",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.current_total_price}
              cellComponent={
                <span className={commonClassesForRowCell(params)}>
                  {`$${params?.data?.current_total_price}`}
                </span>
              }
            />
          ),
        },
        {
          headerName: "Payment Status",
          headerTooltip: "Payment Status",
          field: "orderPaymentStatus",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.id}
              cellComponent={getShopifyOrderFulfillmentStatus(
                params?.data?.orderPaymentStatus
              )}
            />
          ),
          cellStyle: { color: "white" },
          enableSorting: false,
        },
        {
          headerName: "Fulfillment Status",
          headerTooltip: "Fulfillment Status",
          field: "orderFulfillmentStatus",
          cellRenderer: (params) => (
            <CustomCellRendererWithSkeleton
              params={params}
              componentRenderCondition={params?.data?.id}
              cellComponent={getShopifyOrderFulfillmentStatus(
                params?.data?.orderFulfillmentStatus
              )}
            />
          ),
          cellStyle: { color: "white" },
          enableSorting: false,
        },
        getActionColumnOfAgGridReact(actionCellRenderer),
      ];
    },
    // eslint-disable-next-line
    [shopifyOrders, gridState.locationFilter]
  );

  //------- update grid state
  const updateGridState = (gridState) => {
    getShopifyOrders(currentStore?.id, gridState);
    setGridState(gridState);
  };

  //------- get timeframe
  const handleGetTimeFrame = (value) => {
    getTimeFrame(value, timeFrame, setTimeFrame);
  };

  //------- handleSearchInputChange
  const handleSearchInputChange = (value) => {
    setIsSearchLoading(true);
    setDebouncedSearchText(value);
    handleDebouncedSearch(value);
  };

  //--------handleDebouncedSearch
  const handleDebouncedSearch = useDebouncedCallback((value) => {
    // setSearchText(value);
    updateGridState({ ...gridState, searchQuery: value, pageIndex: 1 });
  }, 1000);

  //-------- handle clear search text
  const clearSearchText = () => {
    handleSearchInputChange("");
  };

  const toggleOrderDetailModal = () => {
    setIsShowOrderDetail((prev) => {
      prev && updateGridState(gridState);
      return !prev;
    });
  };

  //------- handle selection of rows on checkbox selection
  const onSelectionChanged = (data) => {
    const selectedNodes = data.api.getSelectedRows();
    setSelectedRows(selectedNodes);
  };

  //-------- function to get data for all teh orders
  const gridData = useMemo(() => {
    let filteredData = shopifyOrders?.data || [];
    const paginationData = shopifyOrders?.paginationData || {};
    setGridState({
      ...gridState,
      pageIndex: paginationData?.pageNumber || defaultGridState?.pageIndex,
      totalRecords:
        paginationData?.totalRecords || defaultGridState?.totalRecords,
      pageEndRecordNumber:
        paginationData?.pageEndRecordNumber ||
        defaultGridState?.pageEndRecordNumber,
      pageStartRecordNumber:
        paginationData?.pageStartRecordNumber ||
        defaultGridState?.pageStartRecordNumber,
    });
    setIsSearchLoading(false);
    setOrderDetail((prev) => {
      let update = {};
      if (prev?.id) {
        update = shopifyOrders?.data?.find((order) => order.id === prev.id);
      }
      return update;
    });
    return filteredData;
    // eslint-disable-next-line
  }, [shopifyOrders]);

  //------- Update GridState on filters chnage or date type
  useEffect(() => {
    if (currentStore?.id && timeFrame?.start && timeFrame?.end) {
      updateGridState({
        ...gridState,
        filters: prepareFilterForShopifyOrdersCall(
          orderType,
          orderPaymentStatus,
          orderFulfillmentStatus,
          timeFrame.start,
          timeFrame.end
        ),
      });
    }
    // eslint-disable-next-line
  }, [
    orderType,
    orderPaymentStatus,
    orderFulfillmentStatus,
    currentStore,
    timeFrame,
  ]);

  return (
    <>
      <Topbar title={"Online Orders"}></Topbar>
      <Row className="m-0">
        <Col md={11} className="d-flex align-items-center gap-3">
          <DashboardDatePicker label="Date" getTimeFrame={handleGetTimeFrame} />{" "}
          {!currentStoreCompany?.isDummyCompany && (
            <CategoriesDropdown
              label="Location Orders"
              options={shopifyConstants.LOCATION_FILTER_OPTIONS}
              handleChange={(selectedOption) => {
                const selectedLocation = selectedOption
                  ? selectedOption.value
                  : shopifyConstants.LOCATION_FILTER_OPTIONS[0].value;
                updateGridState({
                  ...gridState,
                  locationFilter: selectedLocation,
                  pageIndex: 1,
                });
              }}
              noOptionsMessage="No Location Filter Found"
              placeholder="Location Orders"
              SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
              className={"dropdown-max-width"}
              isClearable={false}
              defaultValue={shopifyConstants.LOCATION_FILTER_OPTIONS[0]}
            />
          )}
          <CategoriesDropdown
            label="Order Type"
            options={shopifyConstants.ORDERS_TYPES}
            handleChange={(selectedOption) => {
              if (selectedOption) {
                setOrderType(selectedOption);
              } else {
                setOrderType(ALL_OPTION);
              }
            }}
            noOptionsMessage="No Order Type Found"
            placeholder="Order Type"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <CategoriesDropdown
            label="Payment Status"
            options={getShopifyOrderPaymentStatusOptions()}
            handleChange={(selectedOption) => {
              if (selectedOption) {
                setOrderPaymentStatus(selectedOption);
              } else {
                setOrderPaymentStatus(ALL_OPTION);
              }
            }}
            noOptionsMessage="No Order Status Found"
            placeholder="Order Status"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <CategoriesDropdown
            label="Fulfillment Status"
            options={getShopifyOrderFulfillmentStatusOptions()}
            handleChange={(selectedOption) => {
              if (selectedOption) {
                setOrderFulfillmentStatus(selectedOption);
              } else {
                setOrderFulfillmentStatus(ALL_OPTION);
              }
            }}
            noOptionsMessage="No Order Status Found"
            placeholder="Order Status"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <SearchField
            isFocus={true}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            IconImage={SearchIcon}
            label="Search"
            placeHolder="Search Customer Name or Item"
            value={debouncedSearchText}
            CrossIcon={debouncedSearchText ? CrossIcon : ""}
            clearSearchText={clearSearchText}
            BlueBoxIcon={BlueBoxIcon}
            LoadingIndicator={
              isSearchLoading ||
              (isButtonDisable(
                spinnerArray,
                shopifyConstants.GET_SHOPIFY_ORDERS
              ) &&
                EMPTY_STRING)
                ? LoadingIndicator
                : false
            }
          />
        </Col>
        <Col
          md={1}
          className="d-flex justify-content-end align-items-end gap-3"
        >
          {selectedRows?.length > 0 && (
            <Button
              handleClick={() => {
                handlePrintShopifyPackingSlips(selectedRows, currentStore);
              }}
              IconImage={PrintIcon}
              className={"batch-delete-button"}
            />
          )}
          {/*
        //------- Need for future use
          <Button
            handleClick={() => {}}
            IconImage={EditIcon}
            className={"batch-delete-button"}
          /> */}
        </Col>
        <Col md={12} className="mt-3">
          <ServerPaginationTable
            colDefs={OrdersColumns}
            gridRef={gridRef}
            gridData={
              isSpinnerEnabled(
                spinnerArray,
                shopifyConstants.GET_SHOPIFY_ORDERS
              )
                ? skeletonArray
                : gridData
            }
            selectedRows={selectedRows}
            gridState={gridState}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            singleClickEdit={false}
            updateGridState={updateGridState}
            onSelectionChanged={onSelectionChanged}
            paginationPageSize={gridState.pageSize}
            pageSizeOptions={gridState.pageSizeOptions}
            isDisablePaginationActions={isSpinnerEnabled(
              spinnerArray,
              shopifyConstants.GET_SHOPIFY_ORDERS
            )}
            tableHeight={`calc(100vh - 220px)`}
            tableId={TABLE_IDS.ORDERS}
          />
        </Col>
      </Row>
      {isShowOrderDetail && (
        <OrderDetailsModal
          orderDetail={orderDetail}
          currentStore={currentStore}
          shopifyOrdersCallBackConfig={{
            orderDetail,
            isRefetchOrdersOnSave: true,
            gridState: gridState,
            callBackFuction: (order) => {
              setOrderDetail(order);
            },
          }}
          defaultGridState={gridState}
          isShowOrderDetail={isShowOrderDetail}
          handleMarkOrderAsOpen={handleMarkOrderAsOpen}
          toggleOrderDetailModal={toggleOrderDetailModal}
          handleMarkOrderAsReady={handleMarkOrderAsReady}
          updateShopifyOrderStatus={updateShopifyOrderStatus}
          handleMarkOrderAsFulfilled={handleMarkOrderAsFulfilled}
          handleTransferToLocation={handleTransferToLocation}
        />
      )}
      <ConfirmationModal
        isConfirmationModal={isConfirmationModal}
        title={shopifyConstants.LOCATION_CONFIRMATION.TITLE}
        message={confirmationModalMessage}
        toggleConfirmationModal={toggleConfirmationModal}
        handleNoButtonClick={toggleConfirmationModal}
        handleYesButtonClick={() => {
          toggleConfirmationModal();
          onConfirmActionRef.current();
        }}
      />
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  shopifyOrders: state.shopify.shopifyOrders,
  allCustomers: state.customer.allCustomers,
  currentStoreCompany: state.company.currentStoreCompany,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getShopifyOrders: (storeId, gridState) =>
    dispatch(shopifyActions.getShopifyOrders(storeId, gridState)),
  updateShopifyOrderStatus: (
    storeId,
    data,
    gridState,
    toggleOrderDetailsModal
  ) =>
    dispatch(
      shopifyActions.updateShopifyOrderStatus(
        storeId,
        data,
        gridState,
        toggleOrderDetailsModal
      )
    ),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(Orders);
